import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const requested = useRef(false);

  useEffect(() => {
    if (requested.current) return;
    requested.current = true;
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setMessage(t('verifyEmailInvalid'));
      return;
    }
    axios.post(`${API_URL}/api/auth/verify-email`, { token })
      .then(() => {
        setStatus('success');
        refreshUser();
      })
      .catch((e) => {
        setStatus('error');
        setMessage(e.response?.data?.detail || t('verifyEmailInvalid'));
      });
  }, [searchParams, t, refreshUser]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <Card className="card-floating w-full max-w-md" data-testid="verify-email-page">
        <CardContent className="py-10 flex flex-col items-center text-center gap-4">
          {status === 'loading' && <Loader2 className="w-10 h-10 animate-spin text-blue-500" />}
          {status === 'success' && (
            <>
              <CheckCircle2 className="w-12 h-12 text-emerald-500" />
              <h1 className="text-2xl font-extrabold text-slate-900">{t('verifyEmailSuccessTitle')}</h1>
              <p className="text-slate-500">{t('verifyEmailSuccessDesc')}</p>
            </>
          )}
          {status === 'error' && (
            <>
              <XCircle className="w-12 h-12 text-rose-500" />
              <h1 className="text-2xl font-extrabold text-slate-900">{t('verifyEmailErrorTitle')}</h1>
              <p className="text-slate-500">{message}</p>
            </>
          )}
          {status !== 'loading' && (
            <Button
              onClick={() => navigate('/dashboard')}
              data-testid="verify-email-continue-btn"
              className="rounded-full bg-blue-500 hover:bg-blue-600 mt-2"
            >
              {t('verifyEmailContinue')}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
